import type {
  CashFlowRow,
  DisplayCurrency,
} from "@/hooks/finance/cash-flow/useCashFlow";
import {
  exportStyledExcel,
  type ExportRow,
  type RowKind,
} from "@/components/finance/reports/shared/exportStyledExcel";

type PeriodLabel = { key: string; label: string };

const GRAND_TOTAL_KEYS = new Set(["net-cash-flow", "closing"]);

function kindFor(r: CashFlowRow): RowKind {
  if (r.isHeader) return "header";
  if (GRAND_TOTAL_KEYS.has(r.key)) return "grandTotal";
  if (r.isTotal) return "total";
  return "data";
}

/** Flatten parent rows and their (expandable) children into display order. */
function flatten(rows: CashFlowRow[]): CashFlowRow[] {
  const out: CashFlowRow[] = [];
  for (const r of rows) {
    out.push(r);
    if (r.children?.length) {
      for (const child of r.children) {
        out.push({ ...child, indent: child.indent ?? (r.indent ?? 0) + 1 });
      }
    }
  }
  return out;
}

export function exportCashFlowStatementToExcel(
  rows: CashFlowRow[],
  periodLabels: PeriodLabel[],
  displayCurrency: DisplayCurrency,
  fileName: string
) {
  const exportRows: ExportRow[] = flatten(rows).map((r) => {
    const kind = kindFor(r);
    return {
      label: r.label,
      indent: r.indent ?? 0,
      kind,
      cells:
        kind === "header"
          ? undefined
          : periodLabels.map((p) => ({ value: r.values.get(p.key) ?? 0 })),
    };
  });

  return exportStyledExcel({
    title: "Cash Flow Statement",
    sheetName: "Cash Flow",
    periodLabels,
    rows: exportRows,
    displayCurrency,
    fileName,
  });
}
